import useHighcharts from './useHighcharts'
import { useRef, useEffect, forwardRef, FC } from 'react'
import HighchartsReact, { HighchartsReactProps } from 'highcharts-react-official'
import { ErrorBoundary } from 'react-error-boundary'
import { Typography } from '@mui/material'
import { useHighchartsExport } from './exporting/HighchartsExportContext'
import slugify from 'slugify'

const FallBack = () => (
    <Typography variant="subtitle1" fontWeight="bold" color="text.disabled">
        Impossible d&apos;afficher ce graphique
    </Typography>
)

const HighchartsChart = forwardRef<HighchartsReact.RefObject, Omit<HighchartsReactProps, 'ref' | 'highcharts'>>(
    (props, ref) => {
        const { highchartsInstance } = useHighcharts()
        const isHighchartsInitialized = highchartsInstance !== undefined

        return isHighchartsInitialized ? (
            <ErrorBoundary fallbackRender={FallBack}>
                <HighchartsReact highcharts={highchartsInstance} oneToOne={true} ref={ref} {...props} />
            </ErrorBoundary>
        ) : null
    }
)
HighchartsChart.displayName = 'HighchartsChart'

export const ExportableHighchartsChart = (props: Omit<React.ComponentProps<typeof HighchartsChart>, 'ref'>) => {
    // Must be used within HighchartsExportContext
    const chartComponentRef = useRef<HighchartsReact.RefObject>(null)
    const { register } = useHighchartsExport()

    useEffect(() => {
        if (!chartComponentRef.current?.chart) return
        const chart = chartComponentRef.current.chart

        register([
            { format: 'csv', downloadHandler: () => chart.downloadCSV() },
            { format: 'png', downloadHandler: () => chart.exportChartLocal({ type: 'image/png' }) },
            { format: 'pdf', downloadHandler: () => chart.exportChartLocal({ type: 'application/pdf' }) },
        ])
    }, [register])

    return <HighchartsChart ref={chartComponentRef} {...props} />
}

interface HighchartsChartProps extends Omit<React.ComponentProps<typeof HighchartsChart>, 'ref'> {
    exporting?: boolean
    slug?: string
}

const HighchartsChartWrapper: FC<HighchartsChartProps> = ({ exporting, slug, ...props }) => {
    props.options.exporting = {
        enabled: false,
        filename: slug ? slugify(slug, { lower: true }) : 'chart',
        credits: { enabled: false },
    }
    return exporting ? <ExportableHighchartsChart {...props} /> : <HighchartsChart {...props} />
}

export default HighchartsChartWrapper
